class MobileSwipe extends HTMLElement {
	connectedCallback() {
		this.element = this;
		this.scrollable = this.querySelector('ul');
		this.elements = this.querySelectorAll('li');
		this.currentIndex = 0;
		this.enableNavigation = this.classList.contains('navigation-arrows')
			? true
			: false;

		this.element
			.closest('.product-card')
			?.style.setProperty('--slide-count', this.elements.length);

		if (this.enableNavigation && this.elements.length > 1) {
			this.createNavigationButtons();
		}

		this.observer = new IntersectionObserver(
			(entries, observer) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						this.currentIndex = entry.target.index;
						this.moveBarTo(this.currentIndex);
						document.dispatchEvent(
							new CustomEvent('custom:carousel:index', {
								detail: entry.target.index,
							})
						);
					}
				});
			},
			{
				threshold: 0.51,
			}
		);

		this.elements.forEach((element, index) => {
			// eslint-disable-next-line no-param-reassign
			element.index = index;
			this.observer.observe(element);
			element.addEventListener('dragstart', (e) => e.preventDefault());
		});

		this.initTouch();

		this.addEventListener('touchstart', (e) => {
			this.element
				.querySelectorAll('img[loading]')
				.forEach((el) => el.removeAttribute('loading'));
		});

		this.addEventListener(
			'mouseenter',
			(e) => {
				this.element
					.querySelectorAll('img[loading]')
					.forEach((el) => el.removeAttribute('loading'));
			},
			{ once: true }
		);

		this.addEventListener('mouseenter', (e) => {
			if (this.hover()) {
				this.scrollable.classList.add('scroll-instant');
				this.scrollable.scrollLeft =
					this.element.getBoundingClientRect().width;
				this.scrollable.classList.remove('scroll-instant');
			}
		});

		this.addEventListener('mouseleave', (e) => {
			if (this.hover()) {
				this.scrollable.classList.add('scroll-instant');
				this.scrollable.scrollLeft = 0;
				this.scrollable.classList.remove('scroll-instant');
			}
		});

		if (this.dataset.infinite && this.dataset.infinite === 'true') {
			this.initInfiniteLogic();
		}
	}

	hover() {
		return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
	}

	moveBarTo(index) {
		this.element
			.closest('.product-card')
			?.style.setProperty('--current-index', index);

		if (this.enableNavigation) {
			this.updateNavigationButtons();
		}
	}

	createNavigationButtons() {
		// Create navigation container
		this.navContainer = document.createElement('div');
		this.navContainer.className = 'mobile-swipe-nav';

		// Create left button
		this.leftBtn = document.createElement('button');
		this.leftBtn.className = 'mobile-swipe-btn mobile-swipe-btn--left';
		this.leftBtn.innerHTML = '‹';

		// Create right button
		this.rightBtn = document.createElement('button');
		this.rightBtn.className = 'mobile-swipe-btn mobile-swipe-btn--right';
		this.rightBtn.innerHTML = '›';

		// Add event listeners
		this.leftBtn.addEventListener('click', (e) => {
			e.stopPropagation();
			this.navigateToSlide(this.currentIndex - 1);
		});

		this.rightBtn.addEventListener('click', (e) => {
			e.stopPropagation();
			this.navigateToSlide(this.currentIndex + 1);
		});

		// Append buttons to container
		this.navContainer.appendChild(this.leftBtn);
		this.navContainer.appendChild(this.rightBtn);

		// Add container to element (make sure element has relative positioning)
		this.element.style.position = 'relative';
		this.element.appendChild(this.navContainer);

		// Initial button state update
		this.updateNavigationButtons();
	}

	navigateToSlide(index) {
		if (index < 0 || index >= this.elements.length) return;

		this.elements[index].scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'start',
		});

		this.currentIndex = index;
		this.moveBarTo(index);
		document.dispatchEvent(
			new CustomEvent('custom:carousel:index', { detail: index })
		);
	}

	updateNavigationButtons() {
		if (!this.leftBtn || !this.rightBtn) return;

		// Update left button
		this.leftBtn.style.opacity = this.currentIndex === 0 ? '0.3' : '1';
		this.leftBtn.disabled = this.currentIndex === 0;

		// Update right button
		this.rightBtn.style.opacity =
			this.currentIndex === this.elements.length - 1 ? '0.3' : '1';
		this.rightBtn.disabled = this.currentIndex === this.elements.length - 1;
	}

	initTouch() {
		let isDrag = false;
		let startX = 0;
		let startY = 0;
		let lastX = 0;
		let hasMoved = false;
		let isScrolling;
		let timeout;

		const DRAG_THRESHOLD = 5; // px before we consider it a drag

		const dragStart = (ev) => {
			// Ignore multi-touch (allow pinch zoom)
			if (ev.touches && ev.touches.length > 1) return;

			isDrag = true;
			hasMoved = false;
			isScrolling = undefined;

			this.element.classList.add('snap-none');
			if (timeout) clearTimeout(timeout);

			if (ev.touches) {
				startX = ev.touches[0].clientX;
				startY = ev.touches[0].clientY;
				lastX = startX;
			} else {
				startX = ev.clientX;
				startY = ev.clientY;
				lastX = startX;
			}
		};

		const dragEnd = () => {
			if (!isDrag) return;

			isDrag = false;

			timeout = setTimeout(() => {
				this.element.classList.remove('snap-none');
			}, 400);
		};

		const cancel = () => {
			if (!isDrag) return;
			isDrag = false;
			if (timeout) clearTimeout(timeout);
			this.element.classList.remove('snap-none');
		};

		const drag = (event) => {
			if (!isDrag) return;

			// If multi-touch starts mid-gesture → stop dragging (allow pinch)
			if (event.touches && event.touches.length > 1) {
				isDrag = false;
				this.element.classList.remove('snap-none');
				return;
			}

			const touch = event.touches ? event.touches[0] : event;

			const currentX = touch.clientX;
			const currentY = touch.clientY;

			const diffX = currentX - startX;
			const diffY = currentY - startY;

			if (isScrolling === undefined) {
				isScrolling = Math.abs(diffY) > Math.abs(diffX);
			}

			if (isScrolling) return;

			const deltaX = currentX - lastX;

			// Detect if user has actually dragged
			if (Math.abs(diffX) > DRAG_THRESHOLD) {
				hasMoved = true;
			}

			this.element.scrollLeft -= deltaX * 1.6;
			lastX = currentX;
		};

		const click = function () {
			// Only trigger click if it wasn't a drag
			if (!hasMoved) {
				this.closest('.product-card')
					?.querySelector('.product-card__heading a')
					?.click();
			}
		};

		// Use touch events on touch devices, pointer events only on true pointer (mouse) devices.
		// Registering both on iOS causes double-firing which can trigger page reloads.
		if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
			this.element.addEventListener('touchstart', dragStart, { passive: true });
			this.element.addEventListener('touchmove', drag, { passive: true });
			this.element.addEventListener('touchend', dragEnd, { passive: true });
			this.element.addEventListener('touchcancel', cancel, { passive: true });
		} else {
			this.element.addEventListener('pointerdown', dragStart);
			this.element.addEventListener('pointermove', drag);
			this.element.addEventListener('pointerup', dragEnd);
			this.element.addEventListener('pointercancel', cancel);
		}

		this.element.addEventListener('click', click);

		this.element
			.closest('.product-card')
			?.style.setProperty('cursor', 'pointer');
	}

	initInfiniteLogic() {
		// we want to keep the status bar the same
		// so, duplicate the first and last, and swap them around
		// make sure the intersection observer for them reports the purposeful incorrect current index
		// after the scroll, jump and reset the position
		const firstCopy = this.querySelector('ul > li:first-of-type').cloneNode(true);
		const lastCopy = this.querySelector('ul > li:last-of-type').cloneNode(true);
		this.scrollable.classList.add('force-no-transition');
		requestAnimationFrame(() => {
			this.scrollable.appendChild(firstCopy);
			this.scrollable.prepend(lastCopy);
			firstCopy.classList.add('dummy--first');
			lastCopy.classList.add('dummy--last');
			requestAnimationFrame(() => {
				this.scrollable.scrollLeft = firstCopy.offsetWidth;
				requestAnimationFrame(() => {
					this.scrollable.classList.remove('force-no-transition');

					firstCopy.index = 0;
					this.observer.observe(firstCopy);

					lastCopy.index = firstCopy.previousElementSibling.index;
					this.observer.observe(lastCopy);

					const syncVideos = () => {
						const videos = [...this.scrollable.querySelectorAll('video')];
						if (videos.length > 1) {
							const currentTime = videos[0].currentTime;
							videos.forEach(video => {
								video.currentTime = currentTime;
							})
						}
					};

					this.scrollable.addEventListener('scroll', () => {
						if (this.scrollable.scrollLeft === 0) {
							const offsetLeft = firstCopy.previousElementSibling?.offsetLeft;
							this.scrollable.classList.add('force-no-transition');
							syncVideos();
							requestAnimationFrame(() => {
								this.scrollable.scrollLeft = offsetLeft;
								requestAnimationFrame(() => {
									this.scrollable.classList.remove('force-no-transition');
								});
							});
						} else if (this.scrollable.scrollLeft >= firstCopy.offsetLeft) {
							this.scrollable.classList.add('force-no-transition');
							syncVideos();
							requestAnimationFrame(() => {
								this.scrollable.scrollLeft = firstCopy.offsetWidth;
								requestAnimationFrame(() => {
									this.scrollable.classList.remove('force-no-transition');
								});
							});
						}
					});
				});
			});
		});
	}
}

customElements.define('mobile-swipe', MobileSwipe);
